import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { RefreshCw, RotateCcw, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import FiscalBooksPanel from './FiscalBooksPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sheet-bridge`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const BOOKS = [
  { key: 'work_orders', label: 'Work Orders', idField: 'project_number', cols: ['assigned_manager', 'client_name', 'title'] },
  { key: 'quotes', label: 'Quote Book', idField: 'quote_number', cols: ['estimator', 'client_name', 'description'] },
  { key: 'purchase_orders', label: 'Purchase Orders', idField: 'po_number', cols: ['work_order_number', 'company_name', 'description'] },
];
const ago = (iso) => {
  if (!iso) return 'never';
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  return s < 90 ? `${s}s ago` : s < 5400 ? `${Math.round(s / 60)} min ago` : `${Math.round(s / 3600)} h ago`;
};

export default function SheetBridgePanel({ user }) {
  const [status, setStatus] = useState(null);
  const [removed, setRemoved] = useState({});
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/status`, auth());
      setStatus(r.data);
      const lists = {};
      for (const b of BOOKS) {
        if (r.data.removed_counts?.[b.key]) lists[b.key] = (await axios.get(`${API}/removed`, { ...auth(), params: { book: b.key } })).data;
      }
      setRemoved(lists);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load sheet bridge status'); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const pullNow = async () => {
    setBusy('pull');
    try { const r = await axios.post(`${API}/pull-now`, {}, auth()); toast.success(`Pulled: ${Object.entries(r.data).map(([k, v]) => `${k} ${v}`).join(' · ')}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Pull failed'); } finally { setBusy(''); }
  };
  const restore = async (book, key) => {
    setBusy(`${book}:${key}`);
    try { await axios.post(`${API}/restore`, { book, key }, auth()); toast.success(`${key} restored and written back to the book`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Restore failed'); } finally { setBusy(''); }
  };

  if (!status) return <div className="p-6 text-sm text-slate-500">Loading sheet bridge…</div>;
  return (
    <div className="space-y-4" data-testid="sheet-bridge-panel">
      <FiscalBooksPanel user={user} />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-600 max-w-2xl">Ops reads the three books of {(status.live_years || []).map(y => `FY20${y}`).join(' and ') || 'the current fiscal year'} every <b>{Math.round(status.pull_seconds / 60)} minutes</b>. Shared columns: the sheet wins. Rows cleared in a sheet are hidden here as <i>removed from book</i> (restore below). Deleting in Ops clears the sheet row. Tracked PMs: <b>{status.pms.join(', ')}</b>.</p>
        <button onClick={pullNow} disabled={busy === 'pull'} className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-sky-800 disabled:opacity-50" data-testid="bridge-pull-now">
          {busy === 'pull' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Pull now
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {BOOKS.map(b => {
          const s = status.books?.[b.key] || {};
          const err = s.last_error || s.last_push_error;
          return (
            <div key={b.key} className="ti-panel rounded-xl p-4 space-y-1.5" data-testid={`bridge-book-${b.key}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-slate-900">{b.label}{s.years?.length ? <span className="ml-1.5 text-xs font-semibold text-slate-500">{s.years.map(y => `20${y}`).join(' + ')}</span> : null}</h3>
                {err ? <AlertTriangle className="w-4 h-4 text-amber-600" /> : <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
              </div>
              <p className="text-xs text-slate-600">Last pull <b className="text-slate-900">{ago(s.last_pull_at)}</b>{s.changed ? ' · changed' : ' · no change'} · <b className="text-slate-900">{s.rows ?? '—'}</b> rows</p>
              {s.tabs && <p className="text-xs text-slate-500 truncate">Tabs: {s.tabs.join(', ')}</p>}
              <p className="text-xs text-slate-600">Last push <b className="text-slate-900">{ago(s.last_push_at)}</b> · hidden (removed from book): <b className="text-slate-900">{status.removed_counts?.[b.key] ?? 0}</b></p>
              {err && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 break-words">{err}</p>}
            </div>
          );
        })}
      </div>

      {status.queue?.length > 0 && (
        <div className="ti-panel rounded-xl p-4" data-testid="bridge-queue">
          <h3 className="font-bold text-slate-900 text-sm mb-2">Waiting to write to the sheet ({status.queue.length})</h3>
          <ul className="text-xs text-slate-700 space-y-1">
            {status.queue.map((q, i) => <li key={i}><b>{q.book}</b> {q.op} <span className="font-mono">{q.key}</span> · {q.attempts} attempt{q.attempts === 1 ? '' : 's'} · <span className="text-amber-800">{q.last_error}</span></li>)}
          </ul>
        </div>
      )}

      {BOOKS.filter(b => removed[b.key]?.length).map(b => (
        <div key={b.key} className="ti-panel rounded-xl p-4" data-testid={`bridge-removed-${b.key}`}>
          <h3 className="font-bold text-slate-900 text-sm mb-2">{b.label} — removed from book ({removed[b.key].length})</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <tbody className="divide-y divide-slate-100">
                {removed[b.key].map(r => (
                  <tr key={r[b.idField]}>
                    <td className="py-1.5 pr-3 font-mono font-bold text-slate-900">{r[b.idField]}</td>
                    {b.cols.map(c => <td key={c} className="py-1.5 pr-3 text-slate-600 max-w-[220px] truncate">{r[c]}</td>)}
                    <td className="py-1.5 pr-3 text-slate-400 whitespace-nowrap">{ago(r.removed_from_book_at)}</td>
                    <td className="py-1.5 text-right">
                      <button onClick={() => restore(b.key, r[b.idField])} disabled={busy === `${b.key}:${r[b.idField]}`} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-300 bg-white hover:bg-sky-50 text-slate-800 font-semibold disabled:opacity-50" data-testid={`bridge-restore-${r[b.idField]}`}>
                        <RotateCcw className="w-3 h-3" /> Restore
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
